/**
 * Add money.
 *
 * The flow is manual by design (§0.5, §3): the player transfers money
 * themselves, then tells us about it, and an admin verifying that claim is what
 * credits their balance. So this screen is two steps, presented as two steps —
 * a single form asking for "amount and reference" with no explanation of where
 * to send anything is the version that generates support tickets.
 *
 * Nothing here moves money. Submitting creates a claim.
 */

import { useEffect, useState } from 'react';
import { api, ApiError, intentKey } from '../api/client.ts';
import { errorMessage } from '../api/errors.ts';
import type { PaymentDestinationView } from '../api/types.ts';
import { useT } from '../i18n/index.tsx';
import { etb, parseAmount, amountLessThan, amountGreaterThan } from '../format.ts';
import { haptics } from '../telegram.ts';
import { cleanReference, checkReference } from '../validation.ts';

interface DepositProps {
  onDone(): void;
  onBack(): void;
}

export function Deposit({ onDone, onBack }: DepositProps) {
  const t = useT();
  const [methods, setMethods] = useState<PaymentDestinationView[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    void api
      .depositMethods()
      .then(({ methods: next }) => {
        setMethods(next);
        setSelectedId(next[0]?.id ?? null);
      })
      .catch((err) => setSubmitError(errorMessage(err, t)));
  }, [t]);

  const selected = methods?.find((m) => m.id === selectedId) ?? null;

  async function submit(): Promise<void> {
    if (!selected) return;
    setFieldError(null);
    setSubmitError(null);

    const parsed = parseAmount(amount);
    if (!parsed.ok) {
      setFieldError(parsed.reason === 'empty' ? t.errorAmountRequired : t.errorAmountInvalid);
      return;
    }
    // Checked here so the player is told before a round trip; the server checks
    // independently, because a client-side bound is a convenience and never a
    // control.
    if (selected.minAmount && amountLessThan(parsed.santim, selected.minAmount)) {
      setFieldError(t.errorAmountTooSmall(etb(selected.minAmount)));
      return;
    }
    if (selected.maxAmount && amountGreaterThan(parsed.santim, selected.maxAmount)) {
      setFieldError(t.errorAmountTooLarge(etb(selected.maxAmount)));
      return;
    }

    // Cleaned before checked, and the cleaned value is what is submitted —
    // so the reference stored is the canonical one telebirr's own SMS will
    // carry, which is what makes the automatic match possible at all.
    const ref = cleanReference(reference);
    const refProblem = checkReference(ref);
    if (refProblem) {
      setFieldError(refProblem === 'empty' ? t.errorReferenceRequired : t.errorReferenceInvalid);
      return;
    }

    setSubmitting(true);
    try {
      await api.submitDeposit(
        {
          method: selected.method,
          amount: parsed.santim,
          txReference: ref,
        },
        intentKey('deposit'),
      );
      haptics.win();
      setDone(true);
    } catch (err) {
      setSubmitError(
        err instanceof ApiError && err.code === 'duplicate_reference'
          ? t.errorDuplicateReference
          : errorMessage(err, t),
      );
    } finally {
      setSubmitting(false);
    }
  }

  function copyAccount(): void {
    if (!selected) return;
    void navigator.clipboard?.writeText(selected.account).then(() => {
      haptics.tap();
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  }

  if (done) {
    return (
      <div className="screen screen--centred">
        <div className="outcome__mark outcome__mark--win">✓</div>
        <h1 className="outcome__title">{t.depositSubmitted}</h1>
        <p className="outcome__body">{t.depositSubmittedBody}</p>
        <button type="button" className="button button--primary button--wide" onClick={onDone}>
          {t.done}
        </button>
      </div>
    );
  }

  if (methods !== null && methods.length === 0) {
    // No configured destination means there is genuinely nowhere to send money.
    // Better to say so than to render a form that cannot be completed.
    return (
      <div className="screen screen--centred">
        <h1 className="gate__title">{t.depositNoMethods}</h1>
        <p className="gate__hint">{t.depositNoMethodsHint}</p>
        <button type="button" className="button" onClick={onBack}>
          {t.cancel}
        </button>
      </div>
    );
  }

  return (
    <div className="screen">
      <header className="formhead">
        <button type="button" className="roundhead__back" onClick={onBack} aria-label="Back">
          ‹
        </button>
        <h1 className="formhead__title">{t.depositTitle}</h1>
      </header>

      {methods === null && <p className="activity__empty">{t.connecting}</p>}

      {selected && (
        <>
          {methods && methods.length > 1 && (
            <div className="methodpicker">
              {methods.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={`methodpicker__option${m.id === selectedId ? ' methodpicker__option--on' : ''}`}
                  onClick={() => setSelectedId(m.id)}
                >
                  {m.label}
                </button>
              ))}
            </div>
          )}

          <section className="step">
            <h2 className="step__title">
              <span className="step__number">1</span> {t.depositStep1}
            </h2>
            <p className="step__body">{t.depositStep1Body}</p>

            <div className="destination">
              <div className="destination__row">
                <span className="destination__label">{t.depositAccountNumber}</span>
                <div className="destination__value">
                  <strong className="destination__account">{selected.account}</strong>
                  <button type="button" className="destination__copy" onClick={copyAccount}>
                    {copied ? t.copied : t.copy}
                  </button>
                </div>
              </div>
              {selected.accountName && (
                <div className="destination__row">
                  <span className="destination__label">{t.depositAccountName}</span>
                  {/*
                    Shown so a player can confirm the transfer is arriving at the
                    name they expect before they send. This is the field that
                    defends against a look-alike account posted somewhere.
                  */}
                  <span className="destination__name">{selected.accountName}</span>
                </div>
              )}
              {selected.instructions && (
                <p className="destination__instructions">{selected.instructions}</p>
              )}
              {(selected.minAmount || selected.maxAmount) && (
                <p className="destination__limits">
                  {selected.minAmount && t.depositMinimum(etb(selected.minAmount))}
                  {selected.minAmount && selected.maxAmount && ' · '}
                  {selected.maxAmount && t.depositMaximum(etb(selected.maxAmount))}
                </p>
              )}
            </div>
          </section>

          <section className="step">
            <h2 className="step__title">
              <span className="step__number">2</span> {t.depositStep2}
            </h2>
            <p className="step__body">{t.depositStep2Body}</p>

            <label className="field">
              <span className="field__label">{t.depositAmount}</span>
              <input
                className="field__input"
                // `decimal` rather than `numeric`: it shows a keypad that has a
                // separator, which an amount needs and a PIN does not.
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </label>

            <label className="field">
              <span className="field__label">{t.depositReference}</span>
              <input
                className="field__input"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
              <span className="field__hint">{t.depositReferenceHint}</span>
            </label>
          </section>

          {fieldError && <div className="banner banner--error">{fieldError}</div>}
          {submitError && <div className="banner banner--error">{submitError}</div>}

          <button
            type="button"
            className="button button--primary button--wide"
            onClick={() => void submit()}
            disabled={submitting}
          >
            {submitting ? t.depositSubmitting : t.depositSubmit}
          </button>
        </>
      )}
    </div>
  );
}
